// src/components/admin/onboarding/AdminChecklistConfig.js
// UI redesign of the onboarding checklist configuration page.
//
// Wiring strategy:
//   - Backend currently stores per-item `required | redundant | (absent)`.
//   - Design has three states: Required / Optional / Hidden.
//   - We map:
//       Required ↔ 'required'  (store as 'required')
//       Optional ↔ (absent in config — falls back to item.required from schema)
//       Hidden   ↔ 'redundant' (already excluded by getCompletedItemsCount /
//                               getVisibleChecklistItems)
//   - All writes go through the existing onboardingService.updateChecklistConfig
//     and we dispatch the existing 'onboarding-config-updated' window event so
//     other places that listen (employee wizard, etc.) refresh.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
    User, Briefcase, FileText, Landmark, Shield, Settings, Camera, Lock,
    ChevronDown, Sparkles, Save,
} from 'lucide-react';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { ONBOARDING_CHECKLIST } from '../../../contexts/OnboardingChecklistConfig';
import onboardingService from '../../../services/onboarding.service';
import { palette as buildPalette } from './onboardingTheme';

const PaletteCtx = createContext(buildPalette(false));
const useP = () => useContext(PaletteCtx);

const Card = ({ children, padding = 24, style = {}, ...rest }) => {
    const p = useP();
    return (
        <div {...rest} style={{
            background: p.surface, borderRadius: 12, padding,
            border: `1px solid ${p.border}`, boxShadow: p.cardShadow,
            ...style,
        }}>{children}</div>
    );
};

const Btn = ({ children, variant = 'primary', leftIcon, rightIcon, style = {}, disabled, ...rest }) => {
    const p = useP();
    const variants = {
        primary:   { bg: p.btnPrimaryBg, fg: p.btnPrimaryFg, bd: p.btnPrimaryBd },
        ghost:     { bg: 'transparent',  fg: p.btnGhostFg,   bd: 'transparent' },
        secondary: { bg: p.btnSecondBg,  fg: p.btnSecondFg,  bd: p.btnSecondBd },
    };
    const v = variants[variant] || variants.primary;
    return (
        <button {...rest} disabled={disabled} style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            background: v.bg, color: v.fg, border: `1px solid ${v.bd}`,
            padding: '9px 16px', borderRadius: 8,
            fontSize: 13.5, fontWeight: 600, fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
            ...style,
        }}>{leftIcon}{children}{rightIcon}</button>
    );
};

const SECTION_ICON = {
    basicInfo: User,
    employmentDetails: Briefcase,
    hrLegalForms: Shield,
    identityDocuments: FileText,
    educationalDocuments: FileText,
    payrollBanking: Landmark,
    backgroundVerification: Settings,
    itSystemAccess: Settings,
    otherInformation: Camera,
};

// Map our two stored values + "absent" to the 3-state design vocabulary.
const stateOf = (storedStatus, itemRequired) => {
    if (storedStatus === 'redundant') return 'hidden';
    if (storedStatus === 'required') return 'required';
    // Nothing stored — use the schema default.
    return itemRequired ? 'required' : 'optional';
};

// Inverse: turn the design's 3-state into what we persist.
const toStored = (state) => {
    if (state === 'hidden') return 'redundant';
    if (state === 'required') return 'required';
    return null; // optional → omit from config
};

const AdminChecklistConfig = () => {
    const themeCtx = useTheme();
    const isDark = themeCtx?.isDarkMode ?? (themeCtx?.theme === 'dark');
    const p = useMemo(() => buildPalette(!!isDark), [isDark]);
    const { checklistConfig, fetchChecklistConfig } = useOnboarding();

    // Local edit buffer. Keyed by item.id → 'required' | 'optional' | 'hidden'.
    const [draft, setDraft] = useState({});
    const [collapsed, setCollapsed] = useState({});
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [error, setError] = useState(null);

    // Initialize draft from the persisted config + schema defaults.
    useEffect(() => {
        const initial = {};
        Object.values(ONBOARDING_CHECKLIST).forEach((section) => {
            section.items.forEach((it) => {
                initial[it.id] = stateOf(checklistConfig?.[it.id], it.required);
            });
        });
        setDraft(initial);
    }, [checklistConfig]);

    // Working-state stats for the right-side rail and stat strip.
    const counts = useMemo(() => {
        const c = { required: 0, optional: 0, hidden: 0, total: 0 };
        Object.values(draft).forEach((v) => {
            c.total++;
            if (v === 'required') c.required++;
            else if (v === 'hidden') c.hidden++;
            else c.optional++;
        });
        return c;
    }, [draft]);

    // Pending diff against persisted config.
    const dirtyCount = useMemo(() => {
        let n = 0;
        Object.values(ONBOARDING_CHECKLIST).forEach((section) => {
            section.items.forEach((it) => {
                const persisted = stateOf(checklistConfig?.[it.id], it.required);
                if (draft[it.id] && draft[it.id] !== persisted) n++;
            });
        });
        return n;
    }, [draft, checklistConfig]);

    const setItem = (id, newState) => {
        setDraft((d) => ({ ...d, [id]: newState }));
    };

    const handleDiscard = () => {
        const initial = {};
        Object.values(ONBOARDING_CHECKLIST).forEach((section) => {
            section.items.forEach((it) => {
                initial[it.id] = stateOf(checklistConfig?.[it.id], it.required);
            });
        });
        setDraft(initial);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            // Convert draft to the persisted shape (id -> 'required' | 'redundant').
            const next = {};
            Object.entries(draft).forEach(([id, state]) => {
                const stored = toStored(state);
                if (stored) next[id] = stored;
            });
            await onboardingService.updateChecklistConfig(next);
            // Tell every other place that respects the config to refresh.
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('onboarding-config-updated'));
            }
            await fetchChecklistConfig?.();
            setSavedAt(Date.now());
        } catch (e) {
            setError(e?.message || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    return (
        <PaletteCtx.Provider value={p}>
        <div style={{ minHeight: 'calc(100vh - 80px)', padding: 28, background: p.appBg, fontFamily: 'Inter, system-ui, sans-serif', color: p.textPrimary }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
                <div>
                    <div style={{ fontSize: 12.5, color: p.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>Onboarding settings</div>
                    <h1 style={{ margin: '4px 0 6px', fontSize: 24, fontWeight: 700, letterSpacing: -0.4, color: p.textPrimary }}>Checklist configuration</h1>
                    <p style={{ margin: 0, fontSize: 13.5, color: p.textSecondary, maxWidth: 540 }}>
                        Decide which fields every new employee must fill in. Changes apply to all future hires immediately.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', background: p.surface, borderRadius: 10, border: `1px solid ${p.border}` }}>
                    <Bucket label="Required" count={counts.required} tone="blue" />
                    <Bucket label="Optional" count={counts.optional} tone="gray" />
                    <Bucket label="Hidden"   count={counts.hidden}   tone="red" />
                    <div style={{ width: 1, height: 28, background: p.border }} />
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: p.textMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>Total</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: p.textPrimary }}>{counts.total} fields</div>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 18, alignItems: 'start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {Object.entries(ONBOARDING_CHECKLIST).map(([key, section]) => (
                        <ChecklistSection
                            key={key}
                            sectionKey={key}
                            title={section.title}
                            items={section.items}
                            draft={draft}
                            onChange={setItem}
                            collapsed={!!collapsed[key]}
                            onToggle={() => setCollapsed((c) => ({ ...c, [key]: !c[key] }))}
                        />
                    ))}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 24 }}>
                    <Card padding={18}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: p.textMuted, letterSpacing: 0.6, textTransform: 'uppercase' }}>Live preview</div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: p.textPrimary, marginTop: 4 }}>What employees will see</div>
                        <PreviewList draft={draft} />
                    </Card>

                    <Card padding={16} style={{ background: p.warnBg, border: `1px solid ${p.warnBd}` }}>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Sparkles size={18} style={{ color: '#ca8a04', flexShrink: 0, marginTop: 1 }} />
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: p.warnFg }}>Mid-flight changes?</div>
                                <div style={{ fontSize: 12.5, color: p.warnFg, marginTop: 4, lineHeight: 1.5, opacity: 0.85 }}>
                                    Making a field required after someone has started will show them an "Onboarding hiccup" notice on next login.
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Sticky save bar */}
            <div style={{
                position: 'sticky', bottom: 16, marginTop: 24,
                background: p.surface, border: `1px solid ${p.border}`, borderRadius: 12,
                padding: '12px 16px 12px 18px', display: 'flex', alignItems: 'center', gap: 14,
                boxShadow: '0 8px 24px -8px rgba(0,0,0,0.35)',
            }}>
                <div style={{ width: 8, height: 8, borderRadius: 999, background: dirtyCount > 0 ? '#f59e0b' : '#16a34a' }} />
                <div style={{ fontSize: 13, color: p.textSecondary }}>
                    {error ? (
                        <span style={{ color: '#fca5a5' }}><b>Save failed.</b> {error}</span>
                    ) : dirtyCount > 0 ? (
                        <><b>{dirtyCount} unsaved change{dirtyCount > 1 ? 's' : ''}.</b> Will apply to <span style={{ color: '#60a5fa', fontWeight: 600 }}>all future hires</span>.</>
                    ) : savedAt ? (
                        <><b>Saved.</b> Changes are live.</>
                    ) : (
                        'No pending changes.'
                    )}
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Btn variant="ghost" onClick={handleDiscard} disabled={dirtyCount === 0 || saving}>Discard</Btn>
                    <Btn variant="primary" leftIcon={<Save size={13} />} onClick={handleSave} disabled={dirtyCount === 0 || saving}>
                        {saving ? 'Saving…' : 'Save & publish'}
                    </Btn>
                </div>
            </div>
        </div>
        </PaletteCtx.Provider>
    );
};

/* ── section card ── */
const ChecklistSection = ({ sectionKey, title, items, draft, onChange, collapsed, onToggle }) => {
    const p = useP();
    const Icon = SECTION_ICON[sectionKey] || FileText;
    const reqCount = items.filter((it) => draft[it.id] === 'required').length;
    return (
        <Card padding={0} style={{ overflow: 'hidden' }}>
            <button
                onClick={onToggle}
                style={{
                    all: 'unset', width: '100%',
                    padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 11,
                    background: p.surface2, borderBottom: collapsed ? 'none' : `1px solid ${p.borderSoft}`,
                    cursor: 'pointer',
                }}
            >
                <div style={{ width: 30, height: 30, borderRadius: 7, background: p.pillBlueBg, color: '#60a5fa', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} />
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: p.textPrimary }}>{title}</div>
                    <div style={{ fontSize: 12, color: p.textMuted, marginTop: 1 }}>{items.length} fields · {reqCount} required</div>
                </div>
                <ChevronDown size={16} style={{ color: p.textMuted, transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .12s' }} />
            </button>
            {!collapsed && (
                <div>
                    {items.map((it, i) => (
                        <ChecklistRow
                            key={it.id}
                            item={it}
                            state={draft[it.id] || (it.required ? 'required' : 'optional')}
                            onChange={(newState) => onChange(it.id, newState)}
                            divider={i < items.length - 1}
                        />
                    ))}
                </div>
            )}
        </Card>
    );
};

const ChecklistRow = ({ item, state, onChange, divider }) => {
    const p = useP();
    const locked = item.readonly || item.id === 'employee_id';
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 18px',
            borderBottom: divider ? `1px solid ${p.borderSoft}` : 'none',
        }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: p.textPrimary, display: 'flex', alignItems: 'center', gap: 7 }}>
                    {item.label}
                    {locked && <Lock size={11} style={{ color: p.textMuted }} />}
                </div>
            </div>
            <Segmented value={state} locked={locked} onChange={onChange} />
        </div>
    );
};

const Segmented = ({ value, locked, onChange }) => {
    const p = useP();
    const opts = [
        { id: 'required', label: 'Required', activeFg: p.pillBlueFg,  activeBg: p.pillBlueBg },
        { id: 'optional', label: 'Optional', activeFg: p.textPrimary, activeBg: p.surfaceMute },
        { id: 'hidden',   label: 'Hidden',   activeFg: p.pillRedFg,   activeBg: p.pillRedBg },
    ];
    return (
        <div style={{
            display: 'inline-flex', padding: 3, borderRadius: 8, background: p.surfaceMute,
            opacity: locked ? 0.6 : 1, pointerEvents: locked ? 'none' : 'auto',
        }}>
            {opts.map((o) => {
                const active = value === o.id;
                return (
                    <button
                        key={o.id}
                        onClick={() => onChange(o.id)}
                        style={{
                            padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                            background: active ? o.activeBg : 'transparent', color: active ? o.activeFg : p.textMuted,
                            fontSize: 12, fontWeight: 700, letterSpacing: 0.2,
                            fontFamily: 'inherit',
                        }}
                    >{o.label}</button>
                );
            })}
        </div>
    );
};

const Bucket = ({ label, count, tone }) => {
    const p = useP();
    const palette = {
        blue: { fg: p.pillBlueFg },
        gray: { fg: p.textSecondary },
        red:  { fg: p.pillRedFg },
    }[tone];
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: palette.fg }} />
            <div>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1, color: palette.fg }}>{count}</div>
                <div style={{ fontSize: 11, color: p.textMuted, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
            </div>
        </div>
    );
};

const PreviewList = ({ draft }) => {
    const p = useP();
    const section = ONBOARDING_CHECKLIST.basicInfo;
    const items = section.items.filter((it) => draft[it.id] !== 'hidden');
    return (
        <div style={{ marginTop: 14, padding: 14, background: p.surface2, border: `1px solid ${p.borderSoft}`, borderRadius: 10 }}>
            <div style={{ fontSize: 12, color: p.textMuted, marginBottom: 8, fontWeight: 600 }}>Step 2 of 5 · About you</div>
            {items.map((it) => (
                <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
                    <span style={{ width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${p.inputBorder}` }} />
                    <span style={{ color: p.textSecondary }}>{it.label}</span>
                    {draft[it.id] === 'required' && (
                        <span style={{ marginLeft: 'auto', fontSize: 10, color: '#fca5a5', fontWeight: 700 }}>★</span>
                    )}
                </div>
            ))}
        </div>
    );
};

export default AdminChecklistConfig;
